import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AppButton, CsvFilePicker } from 'components';
import useManageUser from 'hooks/API/useManageUser';
import useUser from 'hooks/API/GET/useUser';
import useToaster from 'hooks/useToaster';
import UserRoleDropDown from 'components/molecules/UserRoleDropDown';
import { checkForErrors } from 'utilities/fns';
import Joi from 'joi';
import ManageRoles2 from './ManageRoles2';
import ManageRoles1 from './ManageRoles1';

function ManageUser() {
  const { userID } = useParams();
  const { isLoading, mutate, isError } = useManageUser();
  const { data } = useUser({ enabled: !!userID, userID });

  useToaster(isLoading, { type: isError ? 'error' : 'success' });

  const [role, handleRole] = useState('');
  const [roleDetails, handleRoleDetails] = useState({});
  const [csvData, setCsvData] = useState(null);
  const [userDetails, setUsersDetails] = useState({});
  const [formErrors, setFormErrors] = useState([]);

  const _roles1 = ['ADMIN', 'CONTENT_CREATOR', 'SUBJECT_EXPERT', 'QC', 'MODERATOR'];
  const _roles2 = ['STUDENT', 'TUTOR'];

  function handleUserSave(apiData = {}) {
    if (isLoading) return;

    let payload = { role, ...apiData };
    if (userID) payload.id = userID;

    const validation = manageRoleSchema.validate(payload, {
      abortEarly: false,
      allowUnknown: true,
    });

    if (validation?.error) {
      setFormErrors(validation.error?.details ?? []);
      return;
    }

    mutate(payload);
    clearData();
  }

  function clearData() {
    handleRole('');
    handleRoleDetails({});
    setUsersDetails({});
  }

  useEffect(() => {
    if (data?.data?.id) {
      const roleObj = data.data.role;
      handleRole(roleObj?._id);
      handleRoleDetails(roleObj);
      setUsersDetails(data.data);
    }
  }, [data]);

  const errors = checkForErrors(formErrors);

  return (
    <div className="mainWraper">
      <div className="container">
        <div className="row">
          <div className="col-6">
            <h2 className="prime-head p-0">Manage User</h2>
          </div>
          <div className="col-6 prime-head text-right">
            <Link to="/manage/users">
              <AppButton variantClass="btn-plane p--tint mx-2">&#8701; Back</AppButton>
            </Link>
          </div>
        </div>

        <div className="mt-4">
          <CsvFilePicker
            text="Upload CSV"
            handleChange={(data) => setCsvData(data)}
            inputProps={{ accept: '.csv' }}
          />
        </div>

        <div className="row my-4">
          <div className="col-lg-4 col-md-6 col-sm-12">
            <UserRoleDropDown
              enableAPI
              value={role}
              handleChange={(event) => {
                const roleObj = JSON.parse(
                  event.target.selectedOptions[0].getAttribute('data-role-obj')
                );
                handleRole(roleObj._id);
                handleRoleDetails(roleObj);
              }}
              fieldErrors={errors['role']}
            />
          </div>
        </div>

        <div className="row my-4">
          {_roles1.includes(roleDetails?.key) && (
            <ManageRoles1
              handleSave={handleUserSave}
              userDetails={userDetails}
              roleKey={roleDetails?.key}
              roleId={roleDetails?._id}
            />
          )}

          {_roles2.includes(roleDetails?.key) && (
            <ManageRoles2
              handleSave={handleUserSave}
              userDetails={userDetails}
              roleKey={roleDetails?.key}
              roleId={roleDetails?._id}
            />
          )}
        </div>
      </div>
    </div>
  );
}

const manageRoleSchema = Joi.object({
  role: Joi.string().required(),
});

ManageUser.defaultProps = {
  handleClass: () => {},
};

export default ManageUser;
